// Plans a "Planned hike from waypoints" route in the background as soon as that's a file's
// choice — by default or picked — so the preview map shows the real route (never a straight
// line) and Import saves it without routing again. The route lives on the file's entry
// (`ImportFileEntry.route`), keyed by the waypoint list it goes through: switching choice and
// back reuses it, a changed list plans a new one, and a removed file or closed dialog just
// drops whatever comes back. The router is injected, so all of this is tested with a fake
// (importRouteRunner.test.ts); the dialog gives it the planner's (runImport `plannerRoute`).

import {
  currentRoute,
  findEntry,
  routeKey,
  routeStops,
  type ImportScreenState,
  type WaypointRoute
} from './importScreenState';
import type { ImportPoint } from './importTypes';
import { concurrencyLimit, routeThroughWaypoints, type WaypointRouter } from './importWaypointRoute';

export interface RouteJob {
  id: string;
  key: string;
  stops: ImportPoint[];
}

/** The hike-from-waypoints files with no route asked for their current waypoints yet. */
export function routesToPlan(s: ImportScreenState): RouteJob[] {
  return s.entries
    .filter(e => e.preview && !e.preview.error && e.settings.choice === 'fromWaypoints' && !currentRoute(e))
    .map(e => {
      const stops = routeStops(e);
      return { id: e.id, key: routeKey(stops), stops };
    });
}

/** Sets a file's route — unless the file has gone, or its waypoints changed since it was asked for. */
export function withRoute(s: ImportScreenState, id: string, route: WaypointRoute): ImportScreenState {
  const e = findEntry(s, id);
  if (!e || route.key !== routeKey(routeStops(e))) return s;
  return { ...s, entries: s.entries.map(x => (x.id === id ? { ...x, route } : x)) };
}

/** Forgets a file's route, so it's planned again ("Try again"). */
export function withRouteCleared(s: ImportScreenState, id: string): ImportScreenState {
  return findEntry(s, id) ? { ...s, entries: s.entries.map(x => (x.id === id ? { ...x, route: null } : x)) } : s;
}

export class WaypointRouteRunner {
  private readonly limit = concurrencyLimit();
  private readonly inFlight = new Map<string, Promise<void>>();
  private disposed = false;

  /**
   * `get` and `set` read and replace the screen state; `set` must not call `sync` back. One
   * `router` is shared by every file, at most four legs at once.
   */
  constructor(
    private readonly router: WaypointRouter,
    private readonly get: () => ImportScreenState,
    private readonly set: (s: ImportScreenState) => void
  ) {}

  /** Starts every route the screen needs that isn't already on its way. Call after each change. */
  sync(): void {
    if (this.disposed) return;
    for (const job of routesToPlan(this.get())) {
      const tag = `${job.id}\n${job.key}`;
      if (this.inFlight.has(tag)) continue;
      this.set(withRoute(this.get(), job.id, { key: job.key, status: 'routing' }));
      // A leg not yet asked for is never asked once the dialog has closed or the file's gone.
      const leg: WaypointRouter = (start, end) =>
        this.limit(() =>
          this.disposed || !findEntry(this.get(), job.id) ? Promise.reject(new Error('cancelled')) : this.router(start, end)
        );
      const run = routeThroughWaypoints(job.stops, leg)
        .then(
          (points): WaypointRoute => ({ key: job.key, status: 'routed', points }),
          (): WaypointRoute => ({ key: job.key, status: 'failed' })
        )
        .then(route => {
          if (!this.disposed) this.set(withRoute(this.get(), job.id, route));
        })
        .finally(() => this.inFlight.delete(tag));
      this.inFlight.set(tag, run);
    }
  }

  /** Plans a file's route again after it failed. */
  retry(id: string): void {
    if (this.disposed) return;
    this.set(withRouteCleared(this.get(), id));
    this.sync();
  }

  /** Resolves once nothing is being routed — what Import waits for before saving. */
  async settled(): Promise<void> {
    while (this.inFlight.size) await Promise.all(Array.from(this.inFlight.values()));
  }

  /** The dialog closed: whatever comes back is dropped, and nothing new starts. */
  dispose(): void {
    this.disposed = true;
  }
}
